<template>
    <div class="deliverySchedule">
        <p v-if="shopEnabled" class="text-center m-0 pt-8 pb-0 px-2">
            {{$t('DELIVERY_SCHEDULE_SHOP')}}
            <router-link :to="{name: 'account.shop'}">
                <span class="underline">
                    {{$t('DELIVERY_SCHEDULE_SHOP_BROWSE')}}
                </span>
            </router-link>
        </p>

        <div v-if="isPaused && !getPauseIsCancel" class="text-center container mx-auto px-2 md:px-16 lg:px-32 xl:px-64 pb-2">
            <p class="text-center m-0 pt-8 pb-0 px-2">
                <unpause-button :subscription="subscription" />
            </p>
        </div>

        <delivery-schedule-address
            v-if="isActive && !isPaused"
            :address="address"
            :show-dropdown="showDropdown"
            address-class="px-4 py-6"/>
        <div v-else class="h-1 lg:h-2"></div>

        <div v-if="isActive && !isPaused">
            <!-- Bank Holiday Messaging -->
            <!-- <div class="message-banner">
                <p class="message-banner__content text-center" v-text="$t('DASHBOARD_WARNING_MESSAGE')"></p>
            </div> -->

            <div class="deliverySchedule__container">
                <div class="mx-auto w-full deliverySchedule__header">
                    <calendar-header/>
                </div>
                <div v-if="isDeliveryDaySelected" class="h-10">
                    <div class="deliverySchedule__card deliverySchedule__card--onPaper"></div>
                </div>
            </div>

            <div class="divider--container" id="portrait-scroll-hook">
                <div class="divider-white--top"></div>
            </div>

            <div class="background--paper py-6">
                <div class="deliverySchedule__container">
                    <div class="deliverySchedule__containerCalendar">
                        <calendar/>
                    </div>

                    <delivery-card-container
                        v-if="isDeliveryDaySelected"
                        :address="address"
                        class="deliverySchedule__card--onPaper"/>
                </div>
                <div id="landscape-scroll-hook"/>
            </div>

            <div class="divider--container">
                <div class="divider-white--bottom"></div>
            </div>
        </div>

        <div>
            <div v-if="isActive && !isPaused">
                <div v-if="isDeliveryDaySelected" class="h-3"></div>

                <div v-if="!isDeliveryDaySelected">
                    <p class="text-center pt-8 pb-6 px-2" v-text="$t('DELIVERY_SCHEDULE_CHOOSE_A_DELIVERY')"></p>
                </div>

                <delivery-card-container
                    v-else
                    :address="address"
                    class="lg:hidden mx-auto w-full clearfix mb-2"/>
            </div>

            <div class="block lg:hidden">
                <div class="divider--container"><div class="divider-white--top"></div></div>
                <div class="background--paper h-6"></div>
                <div class="divider--container"><div class="divider-white--bottom"></div></div>
            </div>

            <manage-delivery
                :key="subscription ? subscription.id : _uid"
                :show-dropdown="showDropdown"
                :is-active="isActive"
                @changed="fetchData"
                @resumed="handleDeliveriesResumed"
                @paused="handleDeliveriesPaused"/>

            <div v-if="shopEnabled">
                <p class="text-center underline p-4 cursor-pointer" @click="openSummaryModal" v-text="$t('SHOP_CALENDAR_ORDER_SUMMARY')"></p>
            </div>

            <div v-if="shopEnabled" class="block text-center">
                <recommendations
                    :className="'calendar'"
                    :headerText="$t('RECOMMENDATIONS_CALENDAR')"
                    :numItems="4">
                </recommendations>

                <button
                    class="product__info__button product__info__button__green"
                    v-text="$t('RECOMMENDATIONS_CALENDAR_SHOP_BUTTON')"
                    @click="goToShop">
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';
import { turnScrollOff } from '@/helpers/ScrollHelpers.js';
import Calendar from '@/components/partials/account/my-delivery-schedule/Calendar';
import ManageDelivery from '@/components/partials/account/my-delivery-schedule/ManageDelivery';
import DeliveryScheduleAddress from '@/components/partials/account/my-delivery-schedule/DeliveryScheduleAddress';
import CalendarHeader from '@/components/partials/account/my-delivery-schedule/calendar/CalendarHeader';
import DeliveryCardContainer from '@/components/partials/account/my-delivery-schedule/deliveryCard/DeliveryCardContainer';
import Recommendations from '../../components/partials/account/shop/Recommendations.vue';
import UnpauseButton from '../../components/partials/account/my-delivery-addresses/UnpauseButton.vue';

export default {
    name: 'DeliverySchedule',
    components: {
        DeliveryScheduleAddress,
        DeliveryCardContainer,
        ManageDelivery,
        CalendarHeader,
        Calendar,
        Recommendations,
        UnpauseButton,
    },
    mounted() {
        this.fetchData();
    },
    data() {
        return {
            isActive: true,
        };
    },
    watch: {
        'subscription.status'() {
            this.isSubscriptionActive();
        },
    },
    computed: {
        ...mapGetters({
            getPauseIsCancel: 'subscription/getPauseIsCancel',
        }),
        activeSubscriptions() {
            return this.$store.getters['subscription/getActiveSubscriptions'];
        },
        address() {
            return this.subscription ? this.$store.getters['address/getAddressById'](this.subscription.delivery_address_id) : {};
        },
        isDeliveryDaySelected() {
            return !isEmpty(this.selectedDelivery);
        },
        selectedDelivery() {
            return this.$store.getters['scheduledOrder/getSelectedDelivery'];
        },
        showDropdown() {
            return this.activeSubscriptions.length > 1;
        },
        subscription() {
            return this.$store.getters['subscription/getActiveSubscription'];
        },
        isPaused() {
            return this.subscription && this.subscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },
        shopEnabled() {
            return process.env.VUE_APP_SHOP_ENABLED === 'true' && !this.isPaused;
        },
    },
    methods: {
        handleDeliveriesPaused() {
            this.isActive = false;
            if (this.activeSubscriptions.length === 1 && this.getPauseIsCancel) {
                this.$store.commit('subscription/setActiveSubscriptionId', this.activeSubscriptions[0].id);
            }
            this.fetchData();
        },
        handleDeliveriesResumed() {
            this.isActive = true;
            this.fetchData();
        },
        isSubscriptionActive() {
            this.isActive = this.subscription.status === SUBSCRIPTION_STATUS_ACTIVE;
        },
        fetchData() {
            this.$store.dispatch('account/fetchUserData', true);
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('cms/fetchPlans', true);
            this.$store.dispatch('scheduledOrder/fetchAllScheduledOrders', true);
        },
        openSummaryModal() {
            this.$store.commit('shop/openOrderSummary');
            turnScrollOff(document);
        },
        goToShop() {
            this.$router.push({ name: 'account.shop' });
        },
    },
};
</script>
